import { createContext, useContext } from 'react'
import { matchPath } from 'react-router-dom'

/** What the account's plan includes (``src/backend/auth/plans.py``). */
export interface AuthPlan {
  id: string
  label: string
  description: string
  /** Workspace routes the plan includes; ``null`` means every screen. */
  screens: string[] | null
  api_tokens: boolean
  requests_per_minute: number | null
  token_requests_per_day: number | null
  /** Most screens, backtests, translations or price fetches of one kind queued or running at once. */
  active_jobs_per_kind?: number | null
}

export interface AuthUser {
  user_id: string
  username: string
  email?: string | null
  role: string
  status: string
  email_verified?: boolean
  plan?: AuthPlan | null
}

/** Used only until /api/auth/status reports the server's configured minimum. */
export const DEFAULT_PASSWORD_MIN_LENGTH = 5

export interface AuthStatus {
  mode: 'disabled' | 'accounts'
  registration_open: boolean
  bootstrap_required: boolean
  password_min_length: number
  /** New accounts must confirm their email address, so registration asks for one. */
  email_verification_required?: boolean
  /** "Forgot your password?" can email a reset link. */
  password_reset_available?: boolean
  /** Who runs the server (the ``legal.*`` settings), for the privacy notice, terms, and footer. */
  operator?: { name: string; contact_email: string; postal_address: string }
}

export interface AuthContextValue {
  user: AuthUser | null
  status: AuthStatus | null
  loading: boolean
  login: (login: string, password: string) => Promise<void>
  register: (username: string, password: string, email?: string) => Promise<AuthUser>
  logout: () => Promise<void>
  /** Reread the signed-in account (after confirming an email address, for example). */
  refreshUser: () => Promise<AuthUser | null>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}

/** Whether the account's plan includes the workspace route at ``pathname`` (``null`` screens: all). */
export function planIncludes(user: AuthUser, pathname: string): boolean {
  const screens = user.plan?.screens
  if (screens == null) return true
  // Bonds moved out of Research without changing the feature's plan entitlement.
  const paths = pathname === '/bonds' ? ['/bonds', '/research'] : [pathname]
  return screens.some(screen => paths.some(path => matchPath({ path: screen, end: true }, path) !== null))
}
